import { useEffect } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import CalendarIcon from "@atlaskit/icon/core/calendar";
import GraphLineIcon from "@atlaskit/icon/core/chart-trend";
import FeedbackIcon from "@atlaskit/icon/core/feedback";
import FileIcon from "@atlaskit/icon/core/file";
import BulletListIcon from "@atlaskit/icon/core/list-bulleted";
import MenuIcon from "@atlaskit/icon/core/menu";
import QuestionCircleIcon from "@atlaskit/icon/core/question-circle";
import RefreshIcon from "@atlaskit/icon/core/refresh";
import RoadmapIcon from "@atlaskit/icon/core/roadmap";
import SendIcon from "@atlaskit/icon/core/send";
import StarFilledIcon from "@atlaskit/icon/core/star-starred";
import IssuesIcon from "@atlaskit/icon/core/tasks";
import { media } from "@atlaskit/primitives";

import { useLocalizedHref } from "@/libraries/web-framework";

import { TemplateKey } from "../../constants/constants";
import {
    getProductDesignModalImage,
    getProductMarketingModalImage,
    getProductOperationsModalImage,
    getProductSoftwareDevelopmentModalImage,
    getUpdatedProductItModalImage
} from "../assets/localized-images";

import {
    OrangeModal,
    PurpleModal,
    GreenModal,
    BlueModal,
    BlackModal
} from "./modal-backgrounds";
import { ProductModalCarousel } from "./modal-carousel";
import { ProductModalCard, ProductModalCardType } from "./product-modal-card";
import { useHeroState } from "./util/hero-context";

export const JswProductModalCarousel = () => {
    const { modalIndex } = useHeroState();
    const intl = useIntl();

    // Disable scroll if modal is open
    useEffect(() => {
        if (modalIndex !== -1) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
    }, [modalIndex]);

    const softwareModal: ProductModalCardType = {
        eyebrow: intl.formatMessage({
            id: "jira-product-tour.hero-modal-software-development.eyebrow-text",
            defaultMessage: "product and issue tracking"
        }),
        title: intl.formatMessage({
            id: "jira-product-tour.hero-modal-software-development.title-text",
            defaultMessage: "Software Development"
        }),
        description: intl.formatMessage({
            id: "jira-product-tour.hero-modal-software-development.description-text",
            defaultMessage:
                "Plan, track, release and support world-class software. Jira is the single source of truth for your entire development lifecycle."
        }),
        image: getProductSoftwareDevelopmentModalImage(intl.locale),
        iconCollection: [
            {
                icon: <GraphLineIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-software-development-icon-analytics.icon-text",
                    defaultMessage: "Analytics"
                })
            },
            {
                icon: <RoadmapIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-software-development-icon-progress-tracking.icon-text",
                    defaultMessage: "Progress tracking"
                })
            },
            {
                icon: <IssuesIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-software-development-icon-sprint-planning.icon-text",
                    defaultMessage: "Sprint planning"
                })
            },
            {
                icon: <MenuIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-software-development-icon-forms.icon-text",
                    defaultMessage: "Forms"
                })
            }
        ],
        BackgroundImage: OrangeModal,
        ctaLink: useLocalizedHref(
            "/try/cloud/signup?bundle=jira-software&jiraProductTourSignupType=software-development"
        ),
        label: "software-development",
        personaData: {
            team: "Software Development",
            persona: "Software Development Generic"
        }
    };

    const marketingModal: ProductModalCardType = {
        eyebrow: intl.formatMessage({
            id: "jira-product-tour.hero-modal-marketing.eyebrow-text",
            defaultMessage: "plan and launch campaigns"
        }),
        title: intl.formatMessage({
            id: "jira-product-tour.hero-modal-marketing.title-text",
            defaultMessage: "Marketing"
        }),
        description: intl.formatMessage({
            id: "jira-product-tour.hero-modal-marketing.description-text",
            defaultMessage:
                "Make launching viral marketing campaigns a breeze. Break down complex campaigns into actionable steps, easily collect requests, and stay aligned with cross-functional teams."
        }),
        image: getProductMarketingModalImage(intl.locale),
        iconCollection: [
            {
                icon: <BulletListIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-marketing-icon-progress-tracking.icon-text",
                    defaultMessage: "Progress tracking"
                })
            },
            {
                icon: <CalendarIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-marketing-icon-calendar-view.icon-text",
                    defaultMessage: "Calendar view"
                })
            },
            {
                icon: <MenuIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-marketing-icon-forms.icon-text",
                    defaultMessage: "Forms"
                })
            },
            {
                icon: <FileIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-marketing-icon-reporting.icon-text",
                    defaultMessage: "Reporting"
                })
            }
        ],
        BackgroundImage: PurpleModal,
        ctaLink: useLocalizedHref(
            `/try/cloud/signup?bundle=jira-software&templateId=${TemplateKey.CampaignManagement}`
        ),
        label: "marketing",
        personaData: {
            team: "Marketing",
            persona: "Marketing Generic"
        }
    };

    const itModal: ProductModalCardType = {
        eyebrow: intl.formatMessage({
            id: "jira-product-tour.hero-modal-it.updated-eyebrow-text",
            defaultMessage: "plan and track it projects"
        }),
        title: intl.formatMessage({
            id: "jira-product-tour.hero-modal-it.title-text",
            defaultMessage: "IT support services"
        }),
        description: intl.formatMessage({
            id: "jira-product-tour.hero-modal-it.updated-description-text",
            defaultMessage:
                "Streamline IT projects and deliver reliable services to internal teams, from migration management to software deployment. Gain visibility across IT workflows to align technology projects to business goals."
        }),
        image: getUpdatedProductItModalImage(intl.locale),
        iconCollection: [
            {
                icon: <RoadmapIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-it-icon-resolve-incidents.updated-icon-text",
                    defaultMessage: "Progress tracking"
                })
            },
            {
                icon: <SendIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-it-icon-help-desk.updated-icon-text",
                    defaultMessage: "Intake requests"
                })
            },
            {
                icon: <RefreshIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-it-icon-manage-requests.updated-icon-text",
                    defaultMessage: "Release management"
                })
            },
            {
                icon: <FeedbackIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-it-icon-reporting.icon-text",
                    defaultMessage: "Reporting"
                })
            }
        ],
        BackgroundImage: GreenModal,
        ctaLink: useLocalizedHref(
            "/try/cloud/signup?bundle=jira-software&jiraProductTourSignupType=it"
        ),
        label: "it",
        personaData: {
            team: "IT Support",
            persona: "IT Support Generic"
        }
    };

    const designModal: ProductModalCardType = {
        eyebrow: intl.formatMessage({
            id: "jira-product-tour.hero-modal-design.eyebrow-text",
            defaultMessage: "build creative workflows"
        }),
        title: intl.formatMessage({
            id: "jira-product-tour.hero-modal-design.title-text",
            defaultMessage: "Design"
        }),
        description: intl.formatMessage({
            id: "jira-product-tour.hero-modal-design.description-text",
            defaultMessage:
                "Collaborate on, organize, and deliver creative requests with ease. Give real-time visibility into designs with Jira’s Figma integration and accelerate approvals."
        }),
        image: getProductDesignModalImage(intl.locale),
        iconCollection: [
            {
                icon: <BulletListIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-design-task-planning.icon-text",
                    defaultMessage: "Task planning"
                })
            },
            {
                icon: <QuestionCircleIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-design-progress-tracking.icon-text",
                    defaultMessage: "Progress tracking"
                })
            },
            {
                icon: <SendIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-design-prioritization.icon-text",
                    defaultMessage: "Prioritization"
                })
            },
            {
                icon: <FileIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-design-forms.icon-text",
                    defaultMessage: "Forms"
                })
            }
        ],
        BackgroundImage: BlueModal,
        ctaLink: useLocalizedHref(
            `/try/cloud/signup?bundle=jira-software&templateId=${TemplateKey.WebDesignProcess}`
        ),
        label: "design",
        personaData: {
            team: "Design",
            persona: "Design Generic"
        }
    };

    const operationsModal: ProductModalCardType = {
        eyebrow: intl.formatMessage({
            id: "jira-product-tour.hero-modal-operations.eyebrow-text",
            defaultMessage: "create intake processes"
        }),
        title: intl.formatMessage({
            id: "jira-product-tour.hero-modal-operations.title-text",
            defaultMessage: "Operations"
        }),
        description: intl.formatMessage({
            id: "jira-product-tour.hero-modal-operations.description-text",
            defaultMessage:
                "Manage procurement, office, and new vendor requests, craft new operational processes, monitor operational risks and more. Keep your company running at maximum efficiency with Jira."
        }),
        image: getProductOperationsModalImage(intl.locale),
        iconCollection: [
            {
                icon: <BulletListIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-operations-task-planning.icon-text",
                    defaultMessage: "Task planning"
                })
            },
            {
                icon: <RoadmapIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-operations-progress-tracking.icon-text",
                    defaultMessage: "Progress tracking"
                })
            },
            {
                icon: <StarFilledIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-operations-prioritization.icon-text",
                    defaultMessage: "Prioritization"
                })
            },
            {
                icon: <MenuIcon size="small" label="" />,
                description: intl.formatMessage({
                    id: "jira-product-tour.hero-modal-operations-forms.icon-text",
                    defaultMessage: "Forms"
                })
            }
        ],
        BackgroundImage: BlackModal,
        ctaLink: useLocalizedHref(
            `/try/cloud/signup?bundle=jira-software&templateId=${TemplateKey.SimplifiedProcessControl}`
        ),
        label: "operations",
        personaData: {
            team: "Operations",
            persona: "Operations Generic"
        }
    };

    const JswProductTourModalCards: Array<ProductModalCardType> = [
        softwareModal,
        marketingModal,
        itModal,
        designModal,
        operationsModal
    ];

    return (
        <div>
            {modalIndex !== -1 && (
                <>
                    {/* desktop */}
                    <ProductModalCarousel>
                        {JswProductTourModalCards.map((props, idx) => {
                            return <ProductModalCard key={idx} {...props} />;
                        })}
                    </ProductModalCarousel>
                    {/* Tablet / Mobile */}
                    <div css={modalBackgroundStyles}>
                        <ProductModalCard
                            {...(JswProductTourModalCards[
                                modalIndex
                            ] as ProductModalCardType)}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

const modalBackgroundStyles = css({
    position: "fixed",
    insetInlineStart: "0",
    insetBlockStart: "0",
    insetInlineEnd: "0",
    insetBlockEnd: "0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
        // eslint-disable-next-line local/ensure-wac-token-usage
        "var(--DarkNeutral-Opaque-DarkNeutral-100, rgba(16, 18, 20, 0.85))",
    zIndex: "10",
    [media.above.lg]: {
        display: "none"
    }
});
